<template>
    <base-tool
        :tool-name="tool.name"
        :tool-description="tool.description"
        @back="goBack"
    >
        <div
            v-if="loadingPipelines"
            class="text-center py-3"
            data-testid="hubspot-ticket-tool-loading"
        >
            <i class="el-icon-loading"></i> Loading HubSpot ticketing pipelines...
        </div>

        <div
            v-else-if="hubspotError"
            class="alert alert-danger"
            data-testid="hubspot-ticket-tool-error"
        >
            <i class="el-icon-warning"></i> {{ hubspotError }}
        </div>

        <div v-else data-testid="hubspot-ticket-tool-form">
            <!-- Pipeline selector -->
            <div class="form-group">
                <label data-testid="hubspot-ticket-tool-pipeline-label">HubSpot Ticketing Pipeline <span class="text-danger">*</span></label>
                <el-select
                    v-model="selectedPipelineId"
                    data-testid="hubspot-ticket-tool-pipeline-select"
                    filterable
                    placeholder="Select a pipeline"
                    class="w-100"
                    @change="handlePipelineChange"
                >
                    <el-option
                        v-for="pipeline in orderedPipelines"
                        :key="pipeline.pipelineId"
                        :label="pipeline.label"
                        :value="pipeline.pipelineId"
                        :data-testid="`hubspot-ticket-tool-pipeline-option-${pipeline.pipelineId}`"
                    >
                        <span>{{ pipeline.label }}</span>
                    </el-option>
                </el-select>
                <small
                    class="form-text text-muted"
                    data-testid="hubspot-ticket-tool-pipeline-help"
                >
                    Select the HubSpot ticketing pipeline where the ticket will be created
                </small>
            </div>

            <!-- Stage selector -->
            <div class="form-group mt-3">
                <label data-testid="hubspot-ticket-tool-stage-label">Ticket Stage <span class="text-danger">*</span></label>
                <el-select
                    v-model="selectedStageId"
                    data-testid="hubspot-ticket-tool-stage-select"
                    filterable
                    placeholder="Select a stage"
                    class="w-100"
                    :disabled="!selectedPipelineId"
                >
                    <el-option
                        v-for="stage in orderedStages"
                        :key="stage.stageId"
                        :label="stage.label"
                        :value="stage.stageId"
                        :data-testid="`hubspot-ticket-tool-stage-option-${stage.stageId}`"
                    >
                        <span>{{ stage.label }}</span>
                    </el-option>
                </el-select>
                <small
                    class="form-text text-muted"
                    data-testid="hubspot-ticket-tool-stage-help"
                >
                    Select the initial stage for the ticket within the chosen pipeline
                </small>
            </div>

            <!-- Action button -->
            <div class="mt-3">
                <el-button
                    data-testid="hubspot-ticket-tool-add-button"
                    type="primary"
                    @click="addTool"
                    :disabled="!isValid"
                >
                    Add {{ tool.name }}
                </el-button>
            </div>
        </div>
    </base-tool>
</template>

<script>
import { mapState } from 'vuex'
import BaseTool from './base-tool.vue'
import ToolMixin from './tool-mixin'

export default {
    name: 'HubspotTicketTool',

    mixins: [ToolMixin],

    components: {
        BaseTool,
    },

    props: {
        tool: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            selectedPipelineId: null,
            selectedStageId: null,
            pipelines: [],
            loadingPipelines: true,
            hubspotError: null,
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        orderedPipelines() {
            if (!this.pipelines || this.pipelines.length === 0) {
                return []
            }

            return [...this.pipelines].sort((a, b) => {
                return (a.displayOrder < b.displayOrder) ? -1 : (a.displayOrder > b.displayOrder) ? 1 : 0
            })
        },

        activePipeline() {
            if (this.pipelines && this.selectedPipelineId) {
                return this.pipelines.find(pipeline => pipeline.pipelineId === this.selectedPipelineId)
            }

            return null
        },

        orderedStages() {
            if (this.activePipeline && this.activePipeline.stages) {
                return this.activePipeline.stages
                    .filter(stage => stage.active === true)
                    .sort((a, b) => {
                        return (a.displayOrder < b.displayOrder) ? -1 : (a.displayOrder > b.displayOrder) ? 1 : 0
                    })
            }

            return []
        },

        selectedStage() {
            if (!this.selectedStageId || !this.activePipeline || !this.activePipeline.stages) {
                return null
            }

            return this.activePipeline.stages.find(s => s.stageId === this.selectedStageId)
        },

        toolPattern() {
            if (!this.activePipeline || !this.selectedStage) {
                return ''
            }

            return `{Create HubSpot ticket: ${this.activePipeline.label}|${this.selectedPipelineId}|${this.selectedStage.label}|${this.selectedStageId}}`
        },

        isValid() {
            return this.selectedPipelineId
                && this.selectedStageId
                && !this.loadingPipelines
                && !this.hubspotError
        }
    },

    mounted() {
        this.loadHubSpotTicketingPipelines()
    },

    methods: {
        handlePipelineChange() {
            // Reset stage when pipeline changes
            this.selectedStageId = null
        },

        async loadHubSpotTicketingPipelines() {
            this.loadingPipelines = true
            this.hubspotError = null

            if (!this.current_company || !this.current_company.hubspot_integration_enabled) {
                this.hubspotError = 'HubSpot integration is not enabled for your account.'
                this.loadingPipelines = false
                return
            }

            try {
                const response = await window.axios.get('/api/v1/integration/hubspot/ticketing-pipelines')

                if (response.data && Array.isArray(response.data)) {
                    this.pipelines = response.data

                    if (this.pipelines.length === 0) {
                        this.hubspotError = 'No HubSpot ticketing pipelines found. Please configure your HubSpot integration first.'
                    }
                } else {
                    this.hubspotError = 'Unable to load HubSpot ticketing pipelines. Please check your HubSpot integration.'
                }
            } catch (error) {
                this.hubspotError = 'Failed to load HubSpot ticketing pipelines. Please try again or contact support.'
            } finally {
                this.loadingPipelines = false
            }
        },

        addTool() {
            this.$emit('hubspot-ticket-selected', {
                text: this.toolPattern
            })
        }
    }
}
</script>

<style scoped>
.form-group {
    margin-bottom: 15px;
}

.alert {
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 15px;
}

.alert-danger {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}
</style>
